import { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Card,
  Select,
  Switch,
  Dropdown,
  Space,
  App,
  Tooltip,
  Badge,
  Row,
  Col,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  MenuOutlined,
  MailOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import usersManagementService, {
  type AccountUser,
  type AddUserRequest,
} from '../../api/usersManagementService';

const ROLES = ['developer', 'collaborator', 'account-admin'] as const;

// Tomorrow.io password rules
const PASSWORD_RULES = [
  {
    id: 'length',
    label: 'Be at least 8 characters long',
    test: (p: string) => p.length >= 8,
  },
  {
    id: 'lowercase',
    label: 'Have at least one lower case letter [a-z]',
    test: (p: string) => /[a-z]/.test(p),
  },
  {
    id: 'uppercase',
    label: 'Have at least one upper case letter [A-Z]',
    test: (p: string) => /[A-Z]/.test(p),
  },
  {
    id: 'numeric',
    label: 'Have at least one numeric character [0-9]',
    test: (p: string) => /[0-9]/.test(p),
  },
  {
    id: 'special',
    label: 'Have at least one special character',
    test: (p: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p),
  },
  {
    id: 'noPersonal',
    label: 'Not include your name or email address',
    test: (p: string, ctx: { firstName?: string; lastName?: string; email?: string }) => {
      if (!p) return true; // empty password doesn't include name/email
      const lower = p.toLowerCase();
      const parts: string[] = [];
      if (ctx.firstName?.trim()) parts.push(ctx.firstName.trim().toLowerCase());
      if (ctx.lastName?.trim()) parts.push(ctx.lastName.trim().toLowerCase());
      if (ctx.email?.trim()) {
        parts.push(ctx.email.trim().toLowerCase());
        const localPart = ctx.email.split('@')[0]?.toLowerCase();
        if (localPart && localPart.length >= 2) parts.push(localPart);
      }
      return !parts.some((part) => part.length >= 2 && lower.includes(part));
    },
  },
] as const;

const allPasswordRulesMet = (
  password: string | undefined,
  ctx: { firstName?: string; lastName?: string; email?: string }
): boolean => {
  const p = password ?? '';
  return PASSWORD_RULES.every((r) =>
    r.id === 'noPersonal' ? r.test(p, ctx) : r.test(p)
  );
};

export const UserManagementPage = () => {
  const { message, modal } = App.useApp();
  const [users, setUsers] = useState<AccountUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AccountUser | null>(null);
  const [addForm] = Form.useForm<AddUserRequest>();
  const [editForm] = Form.useForm<{ firstName: string; lastName: string; phoneNumber: string }>();
  const [phoneValid, setPhoneValid] = useState(true);
  const [editPhoneValid, setEditPhoneValid] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterEmail, setFilterEmail] = useState('');
  const [filterFirstName, setFilterFirstName] = useState('');
  const [filterLastName, setFilterLastName] = useState('');

  // Watch form values so OK button state updates when user types (component re-renders)
  const formValues = Form.useWatch([], addForm);
  const editFormValues = Form.useWatch([], editForm);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await usersManagementService.getUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        message.error(response.errorData?.message || 'Failed to fetch users');
      }
    } catch (error) {
      message.error('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterEmail, filterFirstName, filterLastName]);

  const handleAddUser = async () => {
    try {
      const values = await addForm.validateFields();
      setSaving(true);

      const payload: AddUserRequest = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        password: values.password,
        jobTitle: 'N/A',
        role: values.role || 'collaborator',
        sendForgotPasswordEmail: values.sendForgotPasswordEmail ?? false,
      };
      if (values.phoneNumber?.trim()) {
        payload.phoneNumber = values.phoneNumber.trim();
      }

      const res = await usersManagementService.addUser(payload);
      if (res.success) {
        message.success(res.data ? `User ${res.data.email} added successfully` : 'User added successfully');
        setIsAddModalOpen(false);
        addForm.resetFields();
        fetchUsers();
      } else {
        message.error(res.errorData?.message || 'Failed to add user');
      }
    } catch (error) {
      console.error('Error adding user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = (user: AccountUser) => {
    modal.confirm({
      title: `Delete user ${user.email}?`,
      content: 'This action cannot be undone.',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true);
        try {
          const res = await usersManagementService.deleteUser(user.id);
          if (res.success) {
            message.success('User deleted successfully');
            fetchUsers();
          } else {
            message.error(res.errorData?.message || 'Failed to delete user');
          }
        } catch (error) {
          message.error('Failed to delete user');
          console.error(error);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleToggleActive = async (user: AccountUser, checked: boolean) => {
    setSaving(true);
    try {
      const res = checked
        ? await usersManagementService.activateUser(user.id)
        : await usersManagementService.deactivateUser(user.id);
      if (res.success) {
        message.success(checked ? 'User activated successfully' : 'User deactivated successfully');
        fetchUsers();
      } else {
        message.error(res.errorData?.message || 'Failed to update user status');
      }
    } catch (error) {
      message.error('Failed to update user status');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (userId: string, role: string) => {
    setSaving(true);
    try {
      const res = await usersManagementService.updateUserRole(userId, role);
      if (res.success) {
        message.success('Role updated successfully');
        fetchUsers();
      } else {
        message.error(res.errorData?.message || 'Failed to update role');
      }
    } catch (error) {
      message.error('Failed to update role');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = (user: AccountUser) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phoneNumber: user.phoneNumber || '',
    });
    setEditPhoneValid(true);
    setIsEditModalOpen(true);
  };

  const handleEditUserSubmit = async () => {
    if (!editingUser) return;
    try {
      const values = await editForm.validateFields();
      if (!editPhoneValid) return;
      setSaving(true);

      const payload: { firstName: string; lastName: string; phoneNumber?: string } = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      };
      if (values.phoneNumber?.trim()) {
        payload.phoneNumber = values.phoneNumber.trim();
      }

      const res = await usersManagementService.updateUser(editingUser.id, payload);
      if (res.success) {
        message.success('User updated successfully');
        setIsEditModalOpen(false);
        setEditingUser(null);
        editForm.resetFields();
        fetchUsers();
      } else {
        message.error(res.errorData?.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendForgotPassword = (user: AccountUser) => {
    if (user.authType && user.authType !== 'basic') {
      message.warning('Cannot send password reset: user auth type is not basic');
      return;
    }
    modal.confirm({
      title: `Send password reset email to ${user.email}?`,
      content: 'The user will receive an email to reset their password.',
      okText: 'Send',
      cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true);
        try {
          const res = await usersManagementService.sendForgotPasswordEmail(user.email);
          if (res.success) {
            message.success(`Password reset email sent to ${user.email}`);
          } else {
            message.error(res.errorData?.message || 'Failed to send email');
          }
        } catch (error) {
          message.error('Failed to send email');
          console.error(error);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const actionMenuItems = (record: AccountUser): MenuProps['items'] => [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Edit User',
      onClick: () => handleEditUser(record),
    },
    {
      key: 'forgot-password',
      icon: <MailOutlined />,
      label: 'Send Forgot Password Email',
      disabled: record.authType !== undefined && record.authType !== 'basic',
      onClick: () => handleSendForgotPassword(record),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete User',
      danger: true,
      onClick: () => handleDeleteUser(record),
    },
  ];

  const columns: ColumnsType<AccountUser> = [
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Dropdown menu={{ items: actionMenuItems(record) }} trigger={['click']}>
          <Button type="text" icon={<MenuOutlined />} />
        </Dropdown>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
      filteredValue: filterEmail ? [filterEmail] : undefined,
      filterDropdown: () => (
        <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
          <Input
            placeholder="Search email"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            onPressEnter={(e) => (e as React.KeyboardEvent<HTMLInputElement>).currentTarget.blur()}
            allowClear
          />
          <Space>
            <Button type="primary" size="small" onClick={() => setFilterEmail(filterEmail)}>
              Apply
            </Button>
            <Button size="small" onClick={() => setFilterEmail('')}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        (record.email || '').toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      width: 120,
      filteredValue: filterFirstName ? [filterFirstName] : undefined,
      filterDropdown: () => (
        <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
          <Input
            placeholder="Search first name"
            value={filterFirstName}
            onChange={(e) => setFilterFirstName(e.target.value)}
            onPressEnter={(e) => (e as React.KeyboardEvent<HTMLInputElement>).currentTarget.blur()}
            allowClear
          />
          <Space>
            <Button type="primary" size="small" onClick={() => setFilterFirstName(filterFirstName)}>
              Apply
            </Button>
            <Button size="small" onClick={() => setFilterFirstName('')}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        (record.firstName || '').toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      width: 120,
      filteredValue: filterLastName ? [filterLastName] : undefined,
      filterDropdown: () => (
        <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
          <Input
            placeholder="Search last name"
            value={filterLastName}
            onChange={(e) => setFilterLastName(e.target.value)}
            onPressEnter={(e) => (e as React.KeyboardEvent<HTMLInputElement>).currentTarget.blur()}
            allowClear
          />
          <Space>
            <Button type="primary" size="small" onClick={() => setFilterLastName(filterLastName)}>
              Apply
            </Button>
            <Button size="small" onClick={() => setFilterLastName('')}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        (record.lastName || '').toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 140,
      render: (val: string | undefined) => val || '—',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 160,
      filters: ROLES.map((r) => ({ text: r, value: r })),
      onFilter: (value, record) => record.role === value,
      render: (role, record) => (
        <Select
          value={role}
          style={{ width: 140 }}
          onChange={(newRole) => handleUpdateRole(record.id, newRole)}
          disabled={saving}
          options={ROLES.map((r) => ({ label: r, value: r }))}
        />
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
      render: (isActive: boolean | undefined, record) => (
        <Switch
          checked={isActive === true}
          onChange={(checked) => handleToggleActive(record, checked)}
          disabled={saving}
        />
      ),
    },
  ];

  const isAddFormValid = () => {
    const values = formValues ?? addForm.getFieldsValue();
    const passwordValid =
      values?.password &&
      allPasswordRulesMet(values.password, {
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
      });
    return (
      values?.firstName?.trim() &&
      values?.lastName?.trim() &&
      values?.email?.trim() &&
      passwordValid &&
      phoneValid
    );
  };

  return (
    <div
      style={{
        padding: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Card
        title={
          <Space>
            <Badge count={users.length} offset={[8, 0]} style={{ backgroundColor: '#52c41a' }} />
            <span>User Management</span>
          </Space>
        }
        extra={
          <Space>
            <Tooltip title="Add user">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalOpen(true)}>
                Add User
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading} />
            </Tooltip>
          </Space>
        }
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }}
        bodyStyle={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <Table
          rowKey="id"
          dataSource={users}
          columns={columns}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== undefined) setPageSize(size);
            },
          }}
          scroll={{ x: 900, y: 'calc(100vh - 340px)' }}
        />
      </Card>

      <Modal
        title="Add User"
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onOk={handleAddUser}
        confirmLoading={saving}
        okButtonProps={{ disabled: !isAddFormValid() }}
        destroyOnHidden
      >
        <Form form={addForm} layout="vertical" initialValues={{ role: 'collaborator', sendForgotPasswordEmail: false }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                <Input placeholder="Jane" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                <Input placeholder="Doe" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
            <Input type="email" placeholder="jane@example.com" />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            help={!phoneValid ? 'Format: +1234567890 or +' : undefined}
            validateStatus={!phoneValid ? 'error' : undefined}
          >
            <Input
              placeholder="+1234567890"
              onChange={(e) => {
                const v = e.target.value;
                setPhoneValid(v === '' || /^\+\d*$/.test(v) || v === '+');
              }}
            />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select options={ROLES.map((r) => ({ label: r, value: r }))} />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Password is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const ctx = {
                    firstName: addForm.getFieldValue('firstName'),
                    lastName: addForm.getFieldValue('lastName'),
                    email: addForm.getFieldValue('email'),
                  };
                  if (allPasswordRulesMet(value, ctx)) return Promise.resolve();
                  return Promise.reject(new Error('Password does not meet all requirements'));
                },
              },
            ]}
            validateStatus={
              (() => {
                const p = formValues?.password ?? addForm.getFieldValue('password') ?? '';
                if (!p) return undefined;
                const met = allPasswordRulesMet(p, {
                  firstName: formValues?.firstName ?? addForm.getFieldValue('firstName'),
                  lastName: formValues?.lastName ?? addForm.getFieldValue('lastName'),
                  email: formValues?.email ?? addForm.getFieldValue('email'),
                });
                return met ? 'success' : 'error';
              })()
            }
            help={
              <div style={{ fontSize: 12 }}>
                <div style={{ marginTop: 6, marginBottom: 2, fontWeight: 500 }}>
                  Your password must:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'none', fontSize: 12 }}>
                  {PASSWORD_RULES.map((rule) => {
                    const p = formValues?.password ?? addForm.getFieldValue('password') ?? '';
                    const met =
                      rule.id === 'noPersonal'
                        ? rule.test(p, {
                            firstName: formValues?.firstName ?? addForm.getFieldValue('firstName'),
                            lastName: formValues?.lastName ?? addForm.getFieldValue('lastName'),
                            email: formValues?.email ?? addForm.getFieldValue('email'),
                          })
                        : rule.test(p);
                    return (
                      <li
                        key={rule.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginBottom: 2,
                          color: met ? '#52c41a' : 'rgba(0,0,0,0.45)',
                        }}
                      >
                        {met ? (
                          <CheckCircleOutlined style={{ color: '#52c41a', flexShrink: 0, fontSize: 12 }} />
                        ) : (
                          <MinusCircleOutlined style={{ color: 'rgba(0,0,0,0.25)', flexShrink: 0, fontSize: 12 }} />
                        )}
                        <span>{rule.label}</span>
                      </li>
                    );
                  })}
                </ul>
                <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>
                  Please note: Commonly used passwords are not allowed.
                </div>
              </div>
            }
          >
            <Input.Password placeholder="Password *" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Form.Item name="sendForgotPasswordEmail" noStyle valuePropName="checked">
                <Switch />
              </Form.Item>
              <span>Send forgot password email</span>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit User"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingUser(null);
        }}
        onOk={handleEditUserSubmit}
        confirmLoading={saving}
        okButtonProps={{
          disabled: (() => {
            const v = editFormValues ?? editForm.getFieldsValue();
            return !(v?.firstName?.trim() && v?.lastName?.trim()) || !editPhoneValid;
          })(),
        }}
        destroyOnHidden
      >
        {editingUser && (
          <Form form={editForm} layout="vertical">
            <Form.Item label="Email">
              <Input value={editingUser.email} disabled />
            </Form.Item>
            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
              <Input placeholder="Last name" />
            </Form.Item>
            <Form.Item
              name="phoneNumber"
              label="Phone Number"
              help={!editPhoneValid ? 'Format: +1234567890 or +' : undefined}
              validateStatus={!editPhoneValid ? 'error' : undefined}
            >
              <Input
                placeholder="+1234567890"
                onChange={(e) => {
                  const v = e.target.value;
                  setEditPhoneValid(v === '' || /^\+\d*$/.test(v) || v === '+');
                }}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};
